import type { GameMode } from '@interactive-room';
import { normalizeGameMode } from '@interactive-room';
import type { LinkedSessionRef } from '@interactive-room';

export interface ActivityDraft {
  id: string;
  mode: GameMode;
  title: string;
  subjectTag: string;
  payload: unknown;
  updatedAt: number;
  /** Curso al que pertenece el material (Studio course-centric). */
  courseId?: string;
  /** Sesión de la ruta de aprendizaje vinculada. */
  linkedSession?: LinkedSessionRef;
}

const STORAGE_KEY = 'sala_activity_drafts';

function readAll(): ActivityDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ActivityDraft[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((draft) => {
      const mode = normalizeGameMode(draft.mode);
      return mode ? { ...draft, mode } : draft;
    });
  } catch {
    return [];
  }
}

function writeAll(drafts: ActivityDraft[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
}

export function listActivityDrafts(mode?: GameMode): ActivityDraft[] {
  const all = readAll().sort((a, b) => b.updatedAt - a.updatedAt);
  return mode ? all.filter((d) => d.mode === mode) : all;
}

export function getActivityDraft(id: string): ActivityDraft | null {
  return readAll().find((d) => d.id === id) ?? null;
}

export function saveActivityDraft(draft: Omit<ActivityDraft, 'updatedAt'> & { updatedAt?: number }): ActivityDraft {
  const all = readAll();
  const saved: ActivityDraft = {
    ...draft,
    updatedAt: draft.updatedAt ?? Date.now(),
  };
  const idx = all.findIndex((d) => d.id === saved.id);
  if (idx >= 0) all[idx] = saved;
  else all.push(saved);
  writeAll(all);
  return saved;
}

export function deleteActivityDraft(id: string): void {
  writeAll(readAll().filter((d) => d.id !== id));
}

export function createDraftId(): string {
  return `draft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}
